import React, { useEffect, useState } from 'react';
import NightTrainNetworkMap, { NetworkPlace, NetworkStretch } from '../features/maps/NightTrainNetworkMap';
import { useAuth } from '../features/auth/context';
import { getNightTrainNetworkDetails, NightTrainNetworkDetails } from '../features/admin/api';
import { useDocumentTitle } from '../shared/useDocumentTitle';

// "Night trains in Jody" (backlog #75, first version): one map with every
// night train Jody plans trips with, and four numbers. Public, standalone,
// not linked from the app yet. The network comes simplified from the backend
// (GET /api/public/night-trains/network, backend/services/nightTrains/network.js).
// Which countries and companies the numbers stand for, and how a line is
// counted, are shown to admins only (GET /api/admin/night-trains/network)
// while Paulo decides how much of the coverage to show publicly (2026-10-01).
// Docs: docs/product/night-trains/README.md ("The network map").

// Same convention as the Data sources page: without the prefix the URL
// resolves against the dev server (:3000) instead of the backend (:5050).
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

interface Network {
  version: string;
  stats: {
    lines: number;
    /** Places where a night train stops: stations within 12 km are one place. */
    places: number;
    countries: number;
    companies: number;
  };
  places: NetworkPlace[];
  stretches: NetworkStretch[];
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "27 September 2026" from the package's version, an ISO day. */
function dataDateLabel(version: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(version || '');
  if (!m) return null;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** "a, b and c" */
function listOf(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const HAIRLINE = 'rgba(20,40,30,0.10)';

/** One number of the 2 by 2 panel; `left`/`top` draw the hairlines between cells. */
const Stat: React.FC<{ value: number; label: string; left?: boolean; top?: boolean }> = ({ value, label, left, top }) => (
  <div
    className="flex flex-col justify-center px-4 py-3"
    style={{ borderLeft: left ? `1px solid ${HAIRLINE}` : undefined, borderTop: top ? `1px solid ${HAIRLINE}` : undefined }}
  >
    <div className="text-3xl font-bold leading-none" style={{ color: '#157a55' }}>{value}</div>
    <div className="text-sm text-gray-600 mt-1">{label}</div>
  </div>
);

const GREEN = '#1d9a5c'; // the map's "2–3 lines" green
const INK = '#16241d';
const MUTED = '#6c7872';

/**
 * Your town, a short way to a station nearby, the night train, a station near
 * where you are going: Jody's night trains are not only the direct ones.
 */
const NearbyDiagram: React.FC = () => (
  <svg viewBox="0 0 320 60" width="100%" style={{ maxWidth: 400, display: 'block', margin: '10px 0 4px' }} role="img"
    aria-label="Your town, a short way to a station nearby, a night train, a station nearby, then where you're going">
    <g fontSize={10.5} fill={MUTED}>
      <text x={72} y={14} textAnchor="middle">Station nearby</text>
      <text x={160} y={14} textAnchor="middle" fill="#0a4a2d" fontWeight={600}>🌙 Night train</text>
      <text x={248} y={14} textAnchor="middle">Station nearby</text>
      <text x={2} y={54}>Your town</text>
      <text x={318} y={54} textAnchor="end">Where you're going</text>
    </g>
    <line x1={16} y1={30} x2={66} y2={30} stroke={MUTED} strokeWidth={1.5} strokeDasharray="3 3" />
    <line x1={254} y1={30} x2={304} y2={30} stroke={MUTED} strokeWidth={1.5} strokeDasharray="3 3" />
    <line x1={72} y1={30} x2={248} y2={30} stroke="#ffffff" strokeWidth={8} strokeLinecap="round" />
    <line x1={72} y1={30} x2={248} y2={30} stroke={GREEN} strokeWidth={4.5} strokeLinecap="round" />
    <circle cx={10} cy={30} r={5.5} fill={INK} />
    <circle cx={310} cy={30} r={5.5} fill={INK} />
    <circle cx={72} cy={30} r={4.5} fill="#ffffff" stroke={INK} strokeWidth={1.5} />
    <circle cx={248} cy={30} r={4.5} fill="#ffffff" stroke={INK} strokeWidth={1.5} />
  </svg>
);

/**
 * Jody does not stop at the direct night trains: the plan also offers the
 * ones that stop at a station nearby (up to 80 km away), and "Search night
 * trains" looks farther (150 to 500 km), each with how far the station is
 * (night-trains README, slice 6). Copy: a train "stops", never "route".
 */
const NearbyCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`rounded-lg p-4 ${className}`} style={{ background: '#e7f1ec', color: INK, border: '1px solid #e7f1ec' }}>
    <p className="font-semibold">No night train from your town? Jody looks nearby.</p>
    <NearbyDiagram />
    {/* On a phone the title and the diagram say it; the sentence would push the map down. */}
    <p className="text-sm hidden sm:block" style={{ color: '#3a4a42' }}>
      Jody shows you the direct night trains, and also the ones that stop close to where you start or where you are going, with how far each station is.
    </p>
  </div>
);

const NightTrainMapPage: React.FC = () => {
  useDocumentTitle('Night trains');
  const { user } = useAuth();
  const isAdmin = Boolean(user?.isAdmin);
  const [network, setNetwork] = useState<Network | null>(null);
  const [error, setError] = useState(false);
  const [details, setDetails] = useState<NightTrainNetworkDetails | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    getNightTrainNetworkDetails()
      .then((data) => { if (!cancelled) setDetails(data); })
      .catch(() => { /* the admin block just stays hidden */ });
    return () => { cancelled = true; };
  }, [isAdmin]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/public/night-trains/network`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => { if (!cancelled) setNetwork(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  const date = network ? dataDateLabel(network.version) : null;

  return (
    <div className="min-h-screen py-6 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--bg)' }}>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold" style={{ color: INK }}>Night trains in Jody</h1>
        <p className="text-gray-600 mt-1 max-w-3xl">
          Sleep on board, wake up in a new city. These are the night trains with beds that Jody plans your trips with
          {date ? `, from the timetables of ${date}` : ''}.
        </p>

        {/* Right on top of the map (Paulo, 2026-10-01): two matching panels of
            equal width and height, the numbers (white, 2 by 2, split by
            hairlines) and the "nearby" card (green), same radius and inner
            padding, so their edges line up with each other and with the map.
            On a phone they stack, the card last, above the map. */}
        {network && (
          <div className="mt-5 mb-4 grid gap-4 md:grid-cols-2 md:gap-6">
            <div className="grid grid-cols-2 grid-rows-2 rounded-lg bg-white overflow-hidden" style={{ border: `1px solid ${HAIRLINE}` }}>
              <Stat value={network.stats.countries} label="countries" />
              <Stat value={network.stats.places} label="towns and cities" left />
              <Stat value={network.stats.companies} label="railway companies" top />
              <Stat value={network.stats.lines} label="lines" left top />
            </div>
            <NearbyCard />
          </div>
        )}

        {error && <p className="text-red-600 mt-6">The map could not be loaded. Please try again later.</p>}
        {!error && !network && <p className="text-gray-500 mt-6">Loading…</p>}

        {network && (
          <>
            <NightTrainNetworkMap
              places={network.places}
              stretches={network.stretches}
              height="max(460px, calc(100vh - 250px))"
            />

            <div className="text-sm text-gray-600 mt-4 space-y-2 max-w-4xl">
              <p>
                Lines are drawn straight from station to station, and countries with no timetable data yet, such as Spain, are not on the map.
              </p>
            </div>

            {details && (
              <div className="text-sm text-gray-600 mt-6 space-y-2 max-w-4xl rounded-lg border border-dashed p-4" style={{ borderColor: '#b3bbb6' }}>
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6c7872' }}>Only admins see this</p>
                <p>
                  <span className="font-semibold">Countries:</span> {listOf(details.countries.map((c) => c.name))}.
                </p>
                <p>
                  <span className="font-semibold">Railway companies:</span> {listOf(details.companies)}.
                </p>
                <p>
                  A line is one company's night train between two end stations, both directions together.
                  Stations less than 12 km apart count as one town or city, so Wien Hbf and Wien Meidling count once.
                </p>
                <p>
                  Not on the map yet: night trains in Spain, Portugal, Great Britain and Finland, and Norway's and Romania's own trains.
                  Jody has no timetable data for them yet.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NightTrainMapPage;
